import { Flex, Text, Tooltip } from '@chakra-ui/react';
import BigNumber from 'bignumber.js';
import InfoSVG from './atoms/svg/infoSVG';
import { stringToFormattedNumber } from './stringToNumber';
import { themeVariables } from '@meteraprotocol/sdk/ui/theme.js';

type BuySellFeesProps = {
  type: 'mint' | 'burn';
  managerFee: BigNumber;
  batcherFee: BigNumber;
  platformFee: BigNumber;
  expectedAmount: BigNumber;
  withdrawLovelace: BigNumber;
  mtkTicker: string;
  hasInsuficientToken: boolean;
};

function BuySellFees(props: BuySellFeesProps) {
  const totalFees = props.batcherFee
    .plus(props.managerFee)
    .plus(props.platformFee)
    .abs();

  const formattedUserFee = stringToFormattedNumber(
    props.managerFee
      .abs()
      .div(10 ** 6)
      .toFixed(6),
    6,
    0,
  );
  const formattedBatcherFee = stringToFormattedNumber(
    props.batcherFee
      .abs()
      .div(10 ** 6)
      .toFixed(6),
    6,
    0,
  );
  const formattedPlatformFee = stringToFormattedNumber(
    props.platformFee
      .abs()
      .div(10 ** 6)
      .toFixed(6),
    6,
    0,
  );

  const formattedMTKAmount = stringToFormattedNumber(
    props.expectedAmount
      .abs()
      .div(10 ** 6)
      .toFixed(6),
    6,
    0,
  );
  const formattedADA = stringToFormattedNumber(
    props.withdrawLovelace
      .abs()
      .div(10 ** 6)
      .toFixed(6),
    6,
    0,
  );

  // Minswap fees
  const formattedBatcherFeeMinswap = stringToFormattedNumber(
    BigNumber(2_000_000)
      .div(10 ** 6)
      .toFixed(6),
    6,
    0,
  );
  const formattedDepositMinswap = stringToFormattedNumber(
    BigNumber(2_000_000)
      .div(10 ** 6)
      .toFixed(6),
    6,
    0,
  );

  const userFee = `- ${formattedUserFee} $${props.mtkTicker}`;
  const batcherFee = `- ${formattedBatcherFee} ₳`;
  const platformFee = `- ${formattedPlatformFee} ₳`;
  const expectedMTK = `${formattedMTKAmount} $${props.mtkTicker}`;
  const expectedADA = `${formattedADA} ₳`;
  const amountToolTip =
    props.type === 'mint'
      ? `$${props.mtkTicker} expected to go to your wallet`
      : 'Expected worth of tokens you will receive';
  const minSwapBatcherFee = `${formattedBatcherFeeMinswap} ₳`;
  const minSwapDeposit = `${formattedDepositMinswap} ₳`;
  const minswapSrc =
    'https://sqvzfgobtlzbovqbkaax.supabase.co/storage/v1/object/public/sdk-assets//minswap_horizontal.svg';

  return (
    <Flex
      width={'100%'}
      height={'fit-content'}
      gap={'12px'}
      justifyContent={'space-between'}
      // borderTop={'solid 1px #C0A6FD33'}
      flexDirection={'column'}
    >
      {props.hasInsuficientToken && props.type === 'mint' ? (
        <>
          <Fee
            label="Zapping via"
            info="You are submitting a bulk order via Minswap. Fees are calculated based on Minswap fees"
            imageSrc={minswapSrc}
          ></Fee>
          <Fee
            label="Batching Fees"
            info={`Batching fees might be lower if you hold MIN in your wallet`}
            value={minSwapBatcherFee}
          ></Fee>
          <Fee
            label="Minimum UTxO deposit"
            info={`This amount of ADA will be held as minimum UTxO ADA and will be returned when your orders are processed or cancelled`}
            value={minSwapDeposit}
          ></Fee>
        </>
      ) : (
        <>
          <Fee
            label="Manager Fee"
            info={`$${props.mtkTicker} paid to the Manager`}
            value={userFee}
          />
          <Fee
            label="Batcher Fee"
            info="ADA paid for processing"
            value={batcherFee}
          />
          <Fee
            label="Platform Fee"
            info="ADA paid for placing orders"
            value={platformFee}
          />
          {props.type === 'mint' ? (
            <Fee
              label="Expected Amount"
              info={amountToolTip}
              value={expectedMTK}
            />
          ) : (
            <Fee
              label="Expected worth of tokens"
              info={amountToolTip}
              value={expectedADA}
            />
          )}
        </>
      )}
    </Flex>
  );
}

function Fee({
  label,
  value,
  info,
  imageSrc,
}: {
  label: string;
  value?: string;
  info?: string;
  imageSrc?: string;
}) {
  return (
    <Flex
      justifyContent={'space-between'}
      width={'100%'}
      fontSize="14px"
      fontWeight="400"
      lineHeight="20px"
    >
      <Flex gap={2} alignItems={'center'}>
        <Text color="#A3A3A3">{label}</Text>

        <Tooltip
          placement="top"
          sx={{
            background: 'var(--bg-200)',
            border: '0.5px solid var(--cards-stroke)',
            borderRadius: '0.75rem',
            color: 'var(--off-white-400)',
            fontWeight: '600',
            padding: '1rem',
            backdropFilter: 'blur(30px)',
          }}
          label={info || label}
        >
          <Flex display="flex" align="center" cursor="ponter">
            <InfoSVG />
          </Flex>
        </Tooltip>
      </Flex>
      {imageSrc ? (
        <img src={imageSrc} alt={label} style={{ width: '6rem' }} />
      ) : (
        <Text color={'#FAFAFA'}>{value} </Text>
      )}
    </Flex>
  );
}

export default BuySellFees;
